import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { Form, Button, Row, Col, Card } from 'react-bootstrap';
import { PageHeader, Loading } from '../../components';
import MerchantSelect from '../../components/MerchantSelect/MerchantSelect';
import useComponentState from '../../utils/useComponentState';
import styles from './AddMerchantGroups.module.scss';
import { toast } from 'react-toastify';
import { addMerchantGroupApi, getAllMerchant } from '../../api/merchant';
import { MERCHANT_GROUP_FORM_FIELDS as FORM_FIELDS } from '../../constants/formFields';
import { INITIAL_STATE, RESPONSE_MESSAGE, TOAST_MESSAGES, VALIDATION_ERROR_MESSAGES } from './AddMerchantGroupsConstants'

// Validation rules
const getValidationRules = (state) => [
  { check: () => state.groupName.trim().length > 0, error: VALIDATION_ERROR_MESSAGES.groupName },
  { check: () => Array.isArray(state.merchantIds) && state.merchantIds.length > 0, error: VALIDATION_ERROR_MESSAGES.merchantGroupIdLength },
  { check: () => state.description.trim().length > 0, error: VALIDATION_ERROR_MESSAGES.description },
  { check: () => state.imageUrl.trim().length > 0, error: VALIDATION_ERROR_MESSAGES.imageUrl },
];

// Utility functions
const useFormValidation = (state, setState) => {
  return useCallback(() => {
    const rules = getValidationRules(state);
    const failed = rules.find((rule) => !rule.check());
    if (failed) {
      setState((prev) => ({ ...prev, error: failed.error, validated: true }));
      return false;
    }
    setState((prev) => ({ ...prev, error: null, validated: true }));
    return true;
  }, [state, setState]);
};

// Payload creation
const createPayload = (state) => ({
  merchantGroupId: state.merchantGroupId,
  name: state.groupName,
  description: state.description,
  imageUrl: state.imageUrl,
  merchantIds: state.merchantIds,
});

// Reset form state
const resetFormState = (setState) => {
  setState(INITIAL_STATE);
};

// Form field component
const FormField = ({ field, value, onChange }) => {
  const { name, label, type, placeholder, required, validationMessage, helpText, rows } = field;
  return (
    <Form.Group as={Row} className='mb-3'>
      <Form.Label column sm={3}>
        {label} {required && <span className='text-danger'>*</span>}
      </Form.Label>
      <Col sm={9}>
        <Form.Control
          as={type === 'textarea' ? type : 'input'}
          type={type !== 'textarea' ? type : undefined}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={rows}
          required={required}
        />
        <Form.Control.Feedback type='invalid'>
          {validationMessage}
        </Form.Control.Feedback>
        {helpText && <Form.Text className='text-muted'>{helpText}</Form.Text>}
      </Col>
    </Form.Group>
  );
};

// Form action buttons
const FormActions = ({ onSubmit, onCancel, loading }) => (
  <div className={styles.formActions}>
    <Button variant='secondary' className='me-2' onClick={onCancel}>
      Cancel
    </Button>
    <Button onClick={onSubmit} variant='primary' disabled={loading}>
      {loading ? 'Adding...' : 'Add Merchant Group'}
    </Button>
  </div>
);

// Main component
const AddMerchantGroups = ({ auth }) => {
  const [state, setState] = useComponentState(INITIAL_STATE);
  const validateForm = useFormValidation(state, setState);

  // Fetch merchants on mount
  useEffect(() => {
    const fetchMerchants = async () => {
      try {
        setState({ merchantsLoading: true });
        const response = await getAllMerchant(auth?.auth?.token);
        // Normalize expected array
        const list = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : response?.items || [];
        setState({ merchants: list, merchantsLoading: false });
      } catch (err) {
        setState({ merchantsLoading: false });
        toast.error(TOAST_MESSAGES.merchantsError);
      }
    };
    fetchMerchants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = useCallback((e) => {
    if (!e || !e.target) return;

    const { name, value } = e.target;
    setState({ [name]: value, error: '' });
  }, [setState]);


  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setState({ validated: true });
      return;
    }

    if (!validateForm()) return;

    setState({ loading: true, error: '' });

    try {
      const payload = createPayload(state);
      const response = await addMerchantGroupApi(payload, auth.auth.token);

      // Handle response
      if (response.message === RESPONSE_MESSAGE) {
        toast.success(TOAST_MESSAGES.success);
        resetFormState(setState);
        setState({ success: true, loading: false });
      } else {
        const errorMessage = response.message || TOAST_MESSAGES.error;
        setState({ loading: false, error: errorMessage });
        toast.error(errorMessage);
      }
    } catch (error) {
      const errorMessage = TOAST_MESSAGES.error;
      setState({ loading: false, error: errorMessage });
      toast.error(errorMessage);
    }
  }, [state, validateForm, setState, auth]);

  const handleCancel = useCallback(() => {
    resetFormState(setState);
  }, [setState]);

  // Helper to render merchant dropdown between Group ID and Group Name  
  const renderFormFields = () => {
    const [first, ...rest] = FORM_FIELDS;
    const [groupNameField, ...afterGroupName] = rest;

    return (
      <>
        <FormField
          key={first.name}
          field={first}
          value={state[first.name]}
          onChange={handleInputChange}
        />

        {/* Merchant dropdown inserted here */}
        <MerchantSelect
          merchants={state.merchants}
          selectedMerchants={state.merchantIds}
          onMerchantSelect={(selectedIds) => {
            setState({
              merchantIds: selectedIds,
              error: ''
            });
          }}
          loading={state.merchantsLoading}
          validated={state.validated}
          required={true}
          label="Merchants"
        />

        <FormField
          key={groupNameField.name}
          field={groupNameField}
          value={state[groupNameField.name]}
          onChange={handleInputChange}
        />

        {afterGroupName.map((field) => (
          <FormField
            key={field.name}
            field={field}
            value={state[field.name]}
            onChange={handleInputChange}
          />
        ))}
      </>
    );
  };

  return (
    <div className={styles.root_div}>
      <PageHeader text='Add Merchant Group' />

      <Card className={styles.formCard}>
        <Card.Body>

          <Form noValidate validated={state.validated}>
            <div className='mb-3'>
              <small className='text-muted'>
                Fields marked with <span className='text-danger'>*</span> are required
              </small>
            </div>

            {renderFormFields()}

          </Form>

          {state.loading && <Loading />}
        </Card.Body>
        <FormActions className={styles.formActions} onSubmit={handleSubmit} onCancel={handleCancel} loading={state.loading} />
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(AddMerchantGroups);